import { Box, LoadingSpinner, SimpleGrid } from "@govlaunch/web";
import React, { useEffect } from "react";
import { IBookmarkType, ICollectionFeaturedStatus, ICollectionPackaging } from "~/types/types";
import BundleCollectionCard from "~/src/components/collection/bundleCard/BundleCollectionCard";
import { NetworkStatus, useQuery, useApolloClient } from "@apollo/client";
import moment from "moment";
import ListCollectionsQuery from "~/src/components/collection/graphql/ListCollectionsQuery";
import {
  IListAllCollectionsQuery,
  IListAllCollectionsQueryVariables,
} from "~/src/components/collection/graphql/__generated__/ListCollectionsQuery.generated";
import stringOrFirst from "~/src/utils/stringOrFirst";
import { ICardViewMode } from "~/src/components/collection/cardViewMode";
import { IToggleBookmarkMutationVariables } from "~/lib/mutations/__generated__/ToggleBookmarkMutation.generated";
import ShowMoreButtonWithIcon from "~/components/form/resources/ShowMoreButtonWithIcon";

interface IBundleCollectionsProps {
  query: string | string[];
  toggleBookmark: (bookmarked: boolean, params: { variables: IToggleBookmarkMutationVariables }) => void;
  isMobile?: boolean;
}

export default function BundleCollections({ query, toggleBookmark, isMobile = false }: IBundleCollectionsProps) {
  const apollo = useApolloClient();

  useEffect(() => {
    return () => {
      apollo
        .query({
          fetchPolicy: "network-only",
          query: ListCollectionsQuery,
          variables: bundleVariables,
        })
        .then((response) => {
          if (!response.data) {
            return;
          }

          apollo.writeQuery({
            query: ListCollectionsQuery,
            variables: bundleVariables,
            data: response.data,
          });
        });
    };
  }, []);

  // NOTE: do not touch
  const lastVisitDate = moment().subtract(7, "days").format("YYYY-MM-DD");

  const bundleVariables = {
    filterByType: ICollectionPackaging.Bundle,
    limit: 3,
    cursor: null,
    searchQuery: stringOrFirst(query),
    lastVisitDate: `${lastVisitDate}T00:00:00Z`,
    filterByFeatured: ICollectionFeaturedStatus.FeaturedOnly,
  };

  const {
    data: bundles,
    loading,
    networkStatus,
    fetchMore,
  } = useQuery<IListAllCollectionsQuery, IListAllCollectionsQueryVariables>(ListCollectionsQuery, {
    notifyOnNetworkStatusChange: true,
    variables: bundleVariables,
  });

  if ((loading || networkStatus === NetworkStatus.loading) && !bundles?.listAllCollections.items.length) {
    return (
      <Box p="168px" justifyContent="center" display="flex">
        <LoadingSpinner />
      </Box>
    );
  }

  if (!bundles || bundles.listAllCollections.items.length === 0) {
    return null;
  }

  return (
    <>
      <SimpleGrid columns={[1, 2, 3, 3]} spacing={[6, 6, 6, "72px"]} px={[4, 4, 4, "109px"]} mb={8}>
        {bundles.listAllCollections.items.map((bundle) => (
          <BundleCollectionCard
            key={bundle._id}
            collection={bundle}
            viewMode={isMobile ? ICardViewMode.Mobile : ICardViewMode.Bundles}
            onToggleBookmark={({ _id }) =>
              toggleBookmark(!bundle.viewerDidBookmark, {
                variables: {
                  targetId: _id,
                  targetType: IBookmarkType.Collection,
                },
              })
            }
          />
        ))}
      </SimpleGrid>

      {bundles.listAllCollections.pageInfo.hasNextPage &&
        [NetworkStatus.ready, NetworkStatus.fetchMore].includes(networkStatus) && (
          <Box display="flex" justifyContent="center" mt={0}>
            <ShowMoreButtonWithIcon
              loading={loading}
              onClick={() => {
                fetchMore({
                  variables: {
                    cursor: bundles.listAllCollections.pageInfo.cursor,
                  },
                  updateQuery: (current, { fetchMoreResult }) => {
                    if (!fetchMoreResult || !fetchMoreResult.listAllCollections || !current.listAllCollections) {
                      return {
                        __typename: "Query",
                        listAllCollections: current.listAllCollections,
                      };
                    }

                    return {
                      __typename: "Query",
                      listAllCollections: {
                        ...current.listAllCollections,
                        items: current.listAllCollections.items.concat(fetchMoreResult.listAllCollections.items),
                        pageInfo: {
                          ...fetchMoreResult.listAllCollections.pageInfo,
                        },
                      },
                    };
                  },
                });
              }}
            />
          </Box>
        )}
    </>
  );
}
